import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';

const BulkUpload = ({ userId, existingGroups, onClose }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Auto-close after successful upload
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, onClose]);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    await handleFileUpload(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });

  const buttonClass = "bg-[#04b53a] text-white px-6 py-3 rounded hover:bg-[#039431] transition-colors text-base font-medium min-w-[180px]";

  const downloadTemplate = () => {
    const template = [
      {
        firstName: 'John',
        lastName: 'Smith',
        spouse: 'Jane Smith',
        children: 'Tommy, Sally',
        parents: 'Bob and Mary Smith',
        title: 'Software Engineer',
        company: 'Tech Corp',
        groups: 'Westminster School, Park Synagogue',
        linkedInUrl: 'https://linkedin.com/in/example',
        notes: 'Met at community event',
        imageUrl: 'Optional: URL to image'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'flashcards_template.xlsx');
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    setError(null);
    setSuccess(false);

    try {
      const data = await readExcelFile(file);
      await processCards(data);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Error processing file');
    } finally {
      setUploading(false);
    }
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const data = XLSX.utils.sheet_to_json(worksheet);
          if (data.length === 0) throw new Error('No data found in file');
          resolve(data);
        } catch (err) {
          reject(new Error('Error reading Excel file'));
        }
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsBinaryString(file);
    });
  };

  const processCards = async (cards) => {
    for (const card of cards) {
      const groups = card.groups?.split(',').map(g => g.trim()) || [];
      
      await addDoc(collection(db, 'cards'), {
        userId,
        firstName: card.firstName || '',
        lastName: card.lastName || '',
        spouse: card.spouse || '',
        children: card.children || '',
        parents: card.parents || '',
        title: card.title || '',
        company: card.company || '',
        groups,
        linkedInUrl: card.linkedInUrl || '',
        notes: card.notes || '',
        imageUrl: card.imageUrl || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={downloadTemplate}
          className={buttonClass}
        >
          Download Template
        </button>

        <div 
          {...getRootProps()} 
          className={`flex-1 border-2 border-dashed rounded-lg h-[100px] flex items-center justify-center p-4 ${
            isDragActive ? 'border-[#04b53a] bg-green-50' : 'border-gray-300'
          }`}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <p className="text-center text-gray-600">Drop the file here</p>
          ) : (
            <p className="text-center text-gray-600">
              Drag and drop your Excel file here
            </p>
          )}
        </div>

        <label className={buttonClass + " flex items-center justify-center cursor-pointer"}>
          Choose File
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => handleFileUpload(e.target.files[0])}
            className="hidden"
          />
        </label>
      </div>

      {uploading && <p className="text-gray-600">Uploading cards...</p>}
      {error && <p className="text-red-500">{error}</p>}
      {success && <p className="text-green-500">Cards uploaded successfully! Closing...</p>}
    </div>
  );
};

export default BulkUpload; 